import React, { useState } from 'react';
import toast from 'react-hot-toast';
import ConfirmDialog from './ConfirmDialog';
import { useConfirm } from '../hooks/useConfirm';
import { createDocument, updateDocument, deleteDocument } from '../hooks/useSupabase';
import { 
  ADMIN_ROLES, 
  getAvailableLocations, 
  canCreateAdmin,
  isSuperAdmin,
  createAdminSchema,
  hasPermission,
  ADMIN_PERMISSIONS 
} from '../config/admin';

const AdminManagement = ({ currentAdmin, admins, onAdminCreated, onAdminDeleted }) => {
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showResetPasswordModal, setShowResetPasswordModal] = useState(false);
  const [resettingAdmin, setResettingAdmin] = useState(null);
  const [newPassword, setNewPassword] = useState('');
  const { isOpen, config, confirm, handleClose } = useConfirm();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [editingAdmin, setEditingAdmin] = useState(null);
  const [focusDistricts, setFocusDistricts] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    role: 'DISTRICT_ADMIN',
    allowedLocations: [],
  });
  const availableLocations = getAvailableLocations();

  const resetForm = () => {
    setFormData({
      name: '',
      email: '',
      role: 'DISTRICT_ADMIN',
      allowedLocations: [],
    });
    setEditingAdmin(null);
    setFocusDistricts(false);
    setError('');
  };

  const handleManageDistricts = (admin) => {
    setEditingAdmin(admin);
    setFocusDistricts(true);
    setFormData({
      name: admin.name,
      email: admin.email,
      role: admin.role,
      allowedLocations: admin.allowed_locations || [],
    });
    setShowCreateModal(true);
  };

  const handleResetPassword = (admin) => {
    setResettingAdmin(admin);
    setNewPassword('');
    setShowResetPasswordModal(true);
  };

  const submitPasswordReset = async (e) => {
    e.preventDefault();
    if (!newPassword || newPassword.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }

    setLoading(true);
    try {
      // Update admin record with temp password and force change flag
      // The temp password is checked from DB during login, not Supabase Auth
      await updateDocument('admins', resettingAdmin.id, {
        temp_password: newPassword,
        force_password_change: true,
        password_reset_at: new Date().toISOString(),
        password_reset_by: currentAdmin.email,
      });

      toast.success(
        <div>
          <p className="font-bold">Password reset successful!</p>
          <p className="text-sm mt-1">Temporary password: <strong>{newPassword}</strong></p>
          <p className="text-xs mt-2 text-gray-600">Please share this password with {resettingAdmin.name}</p>
          <p className="text-xs mt-1 text-red-600">They will be forced to change it on next login.</p>
        </div>,
        { duration: 10000 }
      );

      setShowResetPasswordModal(false);
      setResettingAdmin(null);
      setNewPassword('');
    } catch (err) {
      console.error('Error resetting password:', err);
      const errorMsg = err?.message || err?.error || 'Unknown error';
      toast.error(`Failed to reset password: ${errorMsg}`);
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (admin) => {
    setEditingAdmin(admin);
    setFormData({
      name: admin.name,
      email: admin.email,
      role: admin.role,
      allowedLocations: admin.allowed_locations || [],
    });
    setShowCreateModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      if (editingAdmin) {
        await updateDocument('admins', editingAdmin.id, {
          name: formData.name,
          role: formData.role,
          permissions: ADMIN_ROLES[formData.role]?.permissions || [],
          allowed_locations: formData.role === 'SUPER_ADMIN' ? [] : formData.allowedLocations,
        });
        toast.success(focusDistricts ? 'Districts updated successfully' : 'Admin updated successfully');
      } else {
        await createDocument('admins', {
          name: formData.name,
          email: formData.email.toLowerCase(),
          role: formData.role,
          permissions: ADMIN_ROLES[formData.role]?.permissions || [],
          allowed_locations: formData.role === 'SUPER_ADMIN' ? [] : formData.allowedLocations,
          is_active: true,
          created_by: currentAdmin.email,
        });
        toast.success('Admin created successfully');
      }

      setShowCreateModal(false);
      resetForm();
      onAdminCreated?.();
    } catch (err) {
      setError(err.message || 'Failed to save admin');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (adminId) => {
    confirm({
      title: 'Delete Admin?',
      message: 'Are you sure you want to delete this admin? This will revoke their admin privileges.',
      confirmText: 'Yes, Delete',
      cancelText: 'Cancel',
      type: 'danger',
      onConfirm: async () => {
        try {
          await deleteDocument('admins', adminId);
          toast.success('Admin deleted successfully');
          onAdminDeleted?.();
        } catch (error) {
          console.error('Error deleting admin:', error);
          toast.error('Failed to delete admin');
        }
      }
    });
  };

  const toggleAdminStatus = async (admin) => {
    try {
      await updateDocument('admins', admin.id, {
        is_active: !admin.is_active,
      });
    } catch (err) {
      console.error('Error updating admin status:', err);
      alert('Failed to update admin status');
    }
  };

  // Only super admins can manage other admins
  const adminEmail = currentAdmin?.email;
  if (!adminEmail || !isSuperAdmin(adminEmail)) {
    return (
      <div className="p-6 bg-red-50 border border-red-200 rounded-lg">
        <h3 className="text-red-800 font-medium mb-2">Access Denied</h3>
        <p className="text-red-700">Only super admins can manage other admins.</p>
        <p className="text-sm text-red-600 mt-2">Current user email: {adminEmail || 'Not logged in'}</p>
        <p className="text-xs text-gray-600 mt-1">Super admin check result: {isSuperAdmin(adminEmail) ? 'true' : 'false'}</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-bold text-gray-800">Admin Management</h2>
          <p className="text-sm text-gray-500">{admins?.length || 0} admin(s) found</p>
        </div>
        <div className="flex gap-2">
          <button
            onClick={() => onAdminCreated?.()}
            className="px-4 py-2 bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200 transition-colors"
          >
            Refresh
          </button>
          <button
            onClick={() => setShowCreateModal(true)}
            className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 transition-colors shadow-sm flex items-center space-x-2"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
            <span>Add Admin</span>
          </button>
        </div>
      </div>


      {/* Admins List */}
      <div className="bg-white rounded-lg shadow-sm border">
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-semibold text-gray-800">Current Admins</h3>
        </div>

        {admins && admins.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Admin</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Role</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Locations</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Created</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {admins.map((admin) => (
                  <tr key={admin.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div>
                        <div className="text-sm font-medium text-gray-900">{admin.name}</div>
                        <div className="text-sm text-gray-500">{admin.email}</div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-blue-100 text-blue-800">
                        {ADMIN_ROLES[admin.role]?.name || admin.role}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm text-gray-900">
                        {admin.role === 'SUPER_ADMIN' ? (
                          <span className="text-green-600 font-medium">All Locations</span>
                        ) : admin.allowed_locations && admin.allowed_locations.length > 0 ? (
                          <div className="max-w-xs">
                            {admin.allowed_locations.length <= 3 ? (
                              admin.allowed_locations.join(', ')
                            ) : (
                              <>
                                {admin.allowed_locations.slice(0, 2).join(', ')}
                                <span className="text-gray-500"> +{admin.allowed_locations.length - 2} more</span>
                              </>
                            )}
                          </div>
                        ) : (
                          <span className="text-red-600">No locations assigned</span>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                        admin.is_active 
                          ? 'bg-green-100 text-green-800' 
                          : 'bg-red-100 text-red-800'
                      }`}>
                        {admin.is_active ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {admin.created_at ? new Date(admin.created_at).toLocaleDateString() : '—'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium space-x-2">
                      <button
                        onClick={() => handleEdit(admin)}
                        className="text-blue-600 hover:text-blue-900"
                      >
                        Edit
                      </button>
                      <button
                        onClick={() => toggleAdminStatus(admin)}
                        className={`${admin.is_active ? 'text-orange-600 hover:text-orange-900' : 'text-green-600 hover:text-green-900'}`}
                      >
                        {admin.is_active ? 'Deactivate' : 'Activate'}
                      </button>
                      <button
                        onClick={() => handleDelete(admin.id)}
                        className="text-red-600 hover:text-red-900"
                      >
                        Delete
                      </button>
                      {admin.role !== 'SUPER_ADMIN' && (
                        <button
                          onClick={() => handleManageDistricts(admin)}
                          className="text-indigo-600 hover:text-indigo-900"
                          title="Add or remove districts"
                        >
                          📍 Districts
                        </button>
                      )}
                      <button
                        onClick={() => handleResetPassword(admin)}
                        className="text-purple-600 hover:text-purple-900 ml-2"
                        title="Reset Password"
                      >
                        🔑 Reset PW
                      </button>
                      {admin.force_password_change && (
                        <span className="ml-2 text-xs bg-yellow-100 text-yellow-800 px-2 py-1 rounded-full">
                          Must Change PW
                        </span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="p-6 text-center text-gray-500">
            No admins found. Create the first admin to get started.
          </div>
        )}
      </div>

      {/* Role Descriptions */}
      <div className="bg-white rounded-lg shadow-sm border p-6">
        <h3 className="text-lg font-semibold text-gray-800 mb-4">Admin Role Descriptions</h3>
        <div className="space-y-3">
          {Object.entries(ADMIN_ROLES).map(([key, role]) => (
            key !== 'SUPER_ADMIN' && (
              <div key={key} className="border-l-4 border-blue-500 pl-4">
                <h4 className="font-medium text-gray-800">{role.name}</h4>
                <p className="text-sm text-gray-600">{role.description}</p>
                <p className="text-xs text-gray-500 mt-1">
                  {role.locationRestricted ? 'Location-restricted access' : 'Global access'}
                </p>
              </div>
            )
          ))}
        </div>
      </div>

      {/* Create/Edit Admin Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-md w-full p-6">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-xl font-bold text-gray-800">
                {focusDistricts ? `📍 Manage Districts — ${editingAdmin?.name}` : editingAdmin ? 'Edit Admin' : 'Add New Admin'}
              </h3>
              <button
                onClick={() => {
                  setShowCreateModal(false);
                  resetForm();
                }}
                className="text-gray-500 hover:text-gray-700"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {error && (
              <div className="mb-4 p-3 bg-red-50 text-red-700 rounded-md text-sm">
                {error}
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Name</label>
                <input
                  type="text"
                  required
                  value={formData.name}
                  onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-red-500"
                  placeholder="Admin Name"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                <input
                  type="email"
                  required
                  disabled={!!editingAdmin}
                  value={formData.email}
                  onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
                  className={`w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-red-500 ${editingAdmin ? 'bg-gray-100 cursor-not-allowed' : ''}`}
                  placeholder="admin@example.com"
                />
                {editingAdmin && (
                  <p className="text-xs text-gray-500 mt-1">Email cannot be changed.</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Role</label>
                <select
                  value={formData.role}
                  onChange={(e) => setFormData(prev => ({ 
                    ...prev, 
                    role: e.target.value,
                    allowedLocations: e.target.value === 'SUPER_ADMIN' ? [] : prev.allowedLocations
                  }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-red-500"
                >
                  {Object.entries(ADMIN_ROLES).map(([key, role]) => (
                    <option key={key} value={key}>{role.name}</option>
                  ))}
                </select>
              </div>

              {formData.role !== 'SUPER_ADMIN' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Allowed Locations (Districts)
                  </label>
                  <div className="max-h-48 overflow-y-auto border border-gray-200 rounded-md p-2 space-y-2">
                    {availableLocations.map(location => (
                      <label key={location} className="flex items-center space-x-2">
                        <input
                          type="checkbox"
                          checked={formData.allowedLocations.includes(location)}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setFormData(prev => ({
                                ...prev,
                                allowedLocations: [...prev.allowedLocations, location]
                              }));
                            } else {
                              setFormData(prev => ({
                                ...prev,
                                allowedLocations: prev.allowedLocations.filter(l => l !== location)
                              }));
                            }
                          }}
                          className="rounded text-red-600 focus:ring-red-500"
                        />
                        <span className="text-sm text-gray-700">{location}</span>
                      </label>
                    ))}
                  </div>
                </div>
              )}

              <div className="pt-4 flex justify-end space-x-3">
                <button
                  type="button"
                  onClick={() => {
                    setShowCreateModal(false);
                    resetForm();
                  }}
                  className="px-4 py-2 text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={loading}
                  className="px-4 py-2 text-white bg-red-600 rounded-md hover:bg-red-700 transition-colors disabled:opacity-50"
                >
                  {loading ? 'Saving...' : editingAdmin ? 'Update Admin' : 'Create Admin'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Reset Password Modal */}
      {showResetPasswordModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-md w-full p-6">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-xl font-bold text-gray-800">
                🔑 Reset Password
              </h3>
              <button
                onClick={() => {
                  setShowResetPasswordModal(false);
                  setResettingAdmin(null);
                  setNewPassword('');
                }}
                className="text-gray-500 hover:text-gray-700"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <div className="mb-4 p-3 bg-blue-50 text-blue-700 rounded-md text-sm">
              <p className="font-medium">Admin: {resettingAdmin?.name}</p>
              <p>Email: {resettingAdmin?.email}</p>
            </div>

            <form onSubmit={submitPasswordReset} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  New Temporary Password
                </label>
                <input
                  type="text"
                  required
                  minLength={6}
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-red-500"
                  placeholder="Enter minimum 6 characters"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Admin will be forced to change this on next login.
                </p>
              </div>

              <div className="flex gap-3">
                <button
                  type="button"
                  onClick={() => {
                    setShowResetPasswordModal(false);
                    setResettingAdmin(null);
                    setNewPassword('');
                  }}
                  className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={loading || newPassword.length < 6}
                  className="flex-1 px-4 py-2 bg-purple-600 text-white rounded-md hover:bg-purple-700 disabled:opacity-50"
                >
                  {loading ? 'Resetting...' : 'Reset Password'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Confirm Dialog */}
      <ConfirmDialog
        isOpen={isOpen}
        onClose={handleClose}
        onConfirm={config.onConfirm}
        title={config.title}
        message={config.message}
        confirmText={config.confirmText}
        cancelText={config.cancelText}
        type={config.type}
      />
    </div>
  );
};

export default AdminManagement;
